import { array, boolean, discriminatedUnion, enum as enumFactory, int, iso, literal, null as nullFactory, strictObject, string, union } from "../../lazy-zod.js";
import { MAX_TEXT_LENGTH } from "../protocol/constants.js";
import { spaceMentionLabelSchema } from "./space-mention-label.js";
import { spaceTraceExecutionIdentityProjectionSchema } from "../daemon/execution-identity.js";
import { runtimeCoordinatorExecutionCreationDiagnosticSchema, runtimeCoordinatorExecutionEventSchema, runtimeCoordinatorExecutionRecordSchema } from "../daemon/runtime-coordinator-execution.js";
import { targetWakeLifecycleIdentitySchema } from "../protocol/target-wake-lifecycle.js";
import { dispatchExecutionSchema } from "../daemon/conversation-lifecycle.js";
import { openMeldDaemonTaskResultProjectFailureContextSchema } from "../daemon/task-result.js";
import { serviceConnectionTraceDiagnosticsSchema } from "../daemon/service-connection-evidence.js";
import { remoteAgentWorkerRuntimeDiagnosticsResponseSchema } from "../runtime/remote-agent-worker.js";
import { observabilityWakeCorrelationSchema } from "./observability-correlation.js";
//#region src/schemas/http/space-dispatch-trace.ts
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const trimmedNonEmptyStringSchema = string().trim().min(1);
const SPACE_DISPATCH_TRACE_TIMEOUT_POLICY_MAX_MS = 6048e5;
const SPACE_DISPATCH_TRACE_PROMPT_LENGTH_MAX = 1e6;
const SPACE_DISPATCH_TRACE_PROMPT_PREVIEW_MAX_CHARS = 1200;
const SPACE_DISPATCH_TRACE_RESPONSE_CONTRACT_VERSION = 5;
const SPACE_DISPATCH_TRACE_RESPONSE_CONTRACT_VERSION_PREVIOUS = 4;
const SPACE_DISPATCH_TRACE_RESPONSE_CONTRACT_VERSION_HEADER = "x-openmeld-space-dispatch-trace-response-version";
const SPACE_DISPATCH_TRACE_INVALID_OUTPUT_PREVIEW_MAX_CHARS = 180;
const spaceDispatchTraceResponseContractVersionSchema = union([
	literal(2),
	literal(3),
	literal(4),
	literal(5)
]);
const spaceDispatchTraceResponseContractVersionHeaderSchema = enumFactory([
	"2",
	"3",
	String(4),
	String(5)
]).transform(Number).pipe(spaceDispatchTraceResponseContractVersionSchema);
const boundedNullableTrimmedStringSchema = (maxLength) => union([trimmedNonEmptyStringSchema.max(maxLength), nullFactory()]);
const nullableTrimmedStringSchema = boundedNullableTrimmedStringSchema(4096);
const nullableSignalTextSchema = boundedNullableTrimmedStringSchema(MAX_TEXT_LENGTH);
const nullableIsoDateTimeSchema = union([ISO_DATE_TIME_SCHEMA, nullFactory()]);
const nullableIntegerSchema = union([int(), nullFactory()]);
const boundedNullableNonNegativeIntegerSchema = (maxValue) => union([int().min(0).max(maxValue), nullFactory()]);
const nullableProfileKindSchema = union([enumFactory([
	"human",
	"agent",
	"system"
]), nullFactory()]);
const dispatchTraceOwnerSchema = strictObject({
	id: trimmedNonEmptyStringSchema,
	kind: trimmedNonEmptyStringSchema
});
const spaceDispatchTraceDispatchStatusSchema = enumFactory([
	"queued",
	"dispatching",
	"succeeded",
	"failed_retryable",
	"failed_terminal",
	"deduped",
	"blocked",
	"cancelled"
]);
const spaceDispatchTraceAttemptStatusSchema = enumFactory([
	"blocked",
	"dispatching",
	"failed_retryable",
	"failed_terminal",
	"profile_queue_waiting",
	"succeeded",
	"superseded",
	"timed_out",
	"waiting_result"
]);
const spaceDispatchTraceQuerySchema = strictObject({
	spaceId: trimmedNonEmptyStringSchema,
	dispatchId: trimmedNonEmptyStringSchema
});
const spaceDispatchTraceDispatchSchema = strictObject({
	factSource: literal("dispatch_ledger"),
	dispatchId: trimmedNonEmptyStringSchema,
	idempotencyKey: trimmedNonEmptyStringSchema,
	sourceEventId: trimmedNonEmptyStringSchema,
	sourceClientMessageId: nullableTrimmedStringSchema,
	owner: union([dispatchTraceOwnerSchema, nullFactory()]),
	targetProfileId: trimmedNonEmptyStringSchema,
	status: spaceDispatchTraceDispatchStatusSchema,
	firstSeenAt: ISO_DATE_TIME_SCHEMA,
	lastAttemptAt: ISO_DATE_TIME_SCHEMA,
	attemptCount: int().min(0),
	latestAttemptId: nullableTrimmedStringSchema
});
const spaceDispatchTraceLatestAttemptSchema = strictObject({
	factSource: literal("dispatch_attempt"),
	attemptId: trimmedNonEmptyStringSchema,
	dispatchId: trimmedNonEmptyStringSchema,
	idempotencyKey: trimmedNonEmptyStringSchema,
	attemptNo: int().min(1),
	status: spaceDispatchTraceAttemptStatusSchema,
	daemonSessionId: nullableTrimmedStringSchema,
	connectionGeneration: nullableIntegerSchema,
	leaseEpoch: nullableIntegerSchema,
	createdAt: ISO_DATE_TIME_SCHEMA,
	startedAt: nullableIsoDateTimeSchema,
	finishedAt: nullableIsoDateTimeSchema,
	errorCode: nullableTrimmedStringSchema,
	errorMessage: nullableTrimmedStringSchema
});
const spaceDispatchTraceNativeDeliverySchema = strictObject({
	factSource: literal("remote_agent_native_delivery_ledger"),
	deliveryId: trimmedNonEmptyStringSchema,
	dispatchId: trimmedNonEmptyStringSchema,
	idempotencyKey: trimmedNonEmptyStringSchema,
	bindingId: trimmedNonEmptyStringSchema,
	runtimeFamily: literal("hosted_center_agent"),
	status: enumFactory([
		"guardrail_rejected",
		"flag_rejected",
		"worker_unavailable",
		"submitted",
		"accepted",
		"retry_observed",
		"completed",
		"callback_rejected",
		"failed"
	]),
	submissionId: nullableTrimmedStringSchema,
	destination: strictObject({
		conversationKind: enumFactory([
			"message_channel",
			"slack",
			"space"
		]),
		organizationId: trimmedNonEmptyStringSchema,
		spaceId: trimmedNonEmptyStringSchema,
		targetProfileId: trimmedNonEmptyStringSchema
	}),
	controllerNormalization: strictObject({
		factSource: literal("remote_agent_native_delivery_ledger"),
		canonicalAgentControllerRef: literal("hosted:center-agent"),
		reasonCode: literal("hosted_runtime_family_controller_canonicalized"),
		storedAgentControllerRef: nullableTrimmedStringSchema,
		storedAgentControllerRefStatus: enumFactory([
			"canonical",
			"stale",
			"malformed",
			"missing"
		]),
		bindingUpdatedAt: nullableIsoDateTimeSchema
	}),
	createdAt: ISO_DATE_TIME_SCHEMA,
	updatedAt: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceOpsDecisionSchema = strictObject({
	factSource: literal("ops_decision"),
	id: trimmedNonEmptyStringSchema,
	decisionCode: trimmedNonEmptyStringSchema,
	decidedBy: trimmedNonEmptyStringSchema,
	source: trimmedNonEmptyStringSchema,
	decidedAt: ISO_DATE_TIME_SCHEMA,
	correlationId: nullableTrimmedStringSchema,
	expectedRecoveryCriteria: nullableTrimmedStringSchema
});
const spaceDispatchTraceReplySignalSchema = strictObject({
	factSource: enumFactory(["space_signal_by_published_signal_id", "space_signal_recent_window"]),
	signalId: trimmedNonEmptyStringSchema,
	profileId: trimmedNonEmptyStringSchema,
	profileKind: nullableProfileKindSchema,
	profileName: nullableTrimmedStringSchema,
	text: trimmedNonEmptyStringSchema,
	createdAt: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceFinalVisibleMessageFactSourceSchema = enumFactory(["space_signal_by_published_signal_id", "space_final_reply_claim"]);
const spaceDispatchTraceFinalVisibleMessageSchema = strictObject({
	factSource: spaceDispatchTraceFinalVisibleMessageFactSourceSchema,
	signalId: trimmedNonEmptyStringSchema,
	signalType: enumFactory([
		"signal.text",
		"signal.status",
		"signal.system_notice"
	]),
	dispatchId: trimmedNonEmptyStringSchema,
	targetProfileId: trimmedNonEmptyStringSchema,
	profileId: nullableTrimmedStringSchema,
	profileKind: nullableProfileKindSchema,
	profileName: nullableTrimmedStringSchema,
	text: nullableSignalTextSchema,
	reasonCode: nullableTrimmedStringSchema,
	noticeType: union([enumFactory([
		"policy_blocked",
		"stay_silent",
		"terminal_failure",
		"wake_stopped"
	]), nullFactory()]),
	statusType: nullableTrimmedStringSchema,
	createdAt: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceFinalVisibleMessageLinkageStatusSchema = enumFactory([
	"matched_published_signal",
	"matched_final_reply_claim",
	"projection_pending",
	"linkage_missing",
	"storage_inconsistent"
]);
const spaceDispatchTraceFinalVisibleMessageLinkageSourceFactSchema = enumFactory([
	"published_signal_candidate",
	"space_final_reply_claim",
	"none"
]);
const spaceDispatchTraceFinalVisibleMessageLinkageSchema = strictObject({
	status: spaceDispatchTraceFinalVisibleMessageLinkageStatusSchema,
	reasonCode: nullableTrimmedStringSchema,
	sourceFact: spaceDispatchTraceFinalVisibleMessageLinkageSourceFactSchema,
	candidateSignalIds: array(trimmedNonEmptyStringSchema).max(16),
	claimSignalId: nullableTrimmedStringSchema,
	selectedSignalId: nullableTrimmedStringSchema
});
const spaceDispatchTraceFinalVisibleOutcomeStatusSchema = enumFactory([
	"visible_reply",
	"visible_failure",
	"visible_stopped",
	"publication_pending",
	"publication_retrying",
	"publication_unresolved",
	"not_applicable"
]);
const spaceDispatchTraceFinalVisibleOutcomePublicationKindSchema = enumFactory([
	"success_reply",
	"failure_notice",
	"stopped_notice"
]);
const spaceDispatchTraceFinalVisibleOutcomeSourceFactSchema = enumFactory([
	"final_visible_message",
	"result_inbox",
	"runtime_coordinator_execution",
	"runtime_coordinator_event",
	"runtime_coordinator_head_lifecycle",
	"dispatch_result",
	"none"
]);
const spaceDispatchTraceFinalVisibleOutcomeSchema = strictObject({
	delivered: boolean(),
	publicationErrorCode: nullableTrimmedStringSchema,
	publicationKind: union([spaceDispatchTraceFinalVisibleOutcomePublicationKindSchema, nullFactory()]),
	reasonCode: nullableTrimmedStringSchema,
	signalId: nullableTrimmedStringSchema,
	sourceFact: spaceDispatchTraceFinalVisibleOutcomeSourceFactSchema,
	sourceReasonCode: nullableTrimmedStringSchema,
	sourceStatus: nullableTrimmedStringSchema,
	status: spaceDispatchTraceFinalVisibleOutcomeStatusSchema
});
const spaceDispatchTraceClaimLeaseSchema = strictObject({
	factSource: literal("dispatch_claim_lease"),
	claimLeaseId: trimmedNonEmptyStringSchema,
	dispatchId: trimmedNonEmptyStringSchema,
	sourceEventId: trimmedNonEmptyStringSchema,
	sourceClientMessageId: nullableTrimmedStringSchema,
	targetProfileId: trimmedNonEmptyStringSchema,
	bindingId: trimmedNonEmptyStringSchema,
	openMeldServiceRuntimeId: trimmedNonEmptyStringSchema,
	agentControllerRef: trimmedNonEmptyStringSchema,
	leaseState: enumFactory([
		"active",
		"cancelled",
		"expired",
		"released",
		"superseded"
	]),
	claimTraceReason: nullableTrimmedStringSchema,
	createdAt: ISO_DATE_TIME_SCHEMA,
	updatedAt: ISO_DATE_TIME_SCHEMA,
	expiresAt: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceResultSchema = strictObject({
	factSource: literal("dispatch_result"),
	resultRef: trimmedNonEmptyStringSchema,
	dispatchId: trimmedNonEmptyStringSchema,
	resultKind: trimmedNonEmptyStringSchema,
	publicOutcome: trimmedNonEmptyStringSchema,
	publishedSignalId: nullableTrimmedStringSchema,
	statusType: nullableTrimmedStringSchema,
	createdAt: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceResultInboxPublishStateSchema = enumFactory([
	"result_accepted",
	"late_result_available",
	"reply_published",
	"reply_suppressed_stopped",
	"reply_publication_failed",
	"invalid_space_action",
	"legacy_result_quarantined"
]);
const spaceDispatchTraceResultInboxSchema = strictObject({
	factSource: literal("dispatch_result_inbox"),
	resultKey: trimmedNonEmptyStringSchema,
	dispatchId: trimmedNonEmptyStringSchema,
	attemptId: trimmedNonEmptyStringSchema,
	taskId: trimmedNonEmptyStringSchema,
	targetProfileId: trimmedNonEmptyStringSchema,
	daemonSessionId: trimmedNonEmptyStringSchema,
	leaseEpoch: int(),
	submittedConnectionGeneration: int(),
	projectFailureContext: openMeldDaemonTaskResultProjectFailureContextSchema.optional(),
	publishState: spaceDispatchTraceResultInboxPublishStateSchema,
	publishedSignalId: nullableTrimmedStringSchema,
	publicationErrorCode: nullableTrimmedStringSchema,
	acceptedAt: ISO_DATE_TIME_SCHEMA,
	updatedAt: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceStreamingSchema = strictObject({
	streamingMode: enumFactory(["final", "stream"]),
	previewEmitted: boolean(),
	firstStdoutAt: nullableIsoDateTimeSchema,
	firstOutputAt: nullableIsoDateTimeSchema
});
const spaceDispatchTraceRuntimeSchema = strictObject({
	processId: nullableIntegerSchema,
	processSpawnedAt: nullableIsoDateTimeSchema,
	stdinClosedAt: nullableIsoDateTimeSchema,
	firstStderrAt: nullableIsoDateTimeSchema,
	exitObservedAt: nullableIsoDateTimeSchema,
	exitCode: nullableIntegerSchema,
	exitSignal: nullableTrimmedStringSchema,
	timeoutTriggeredAt: nullableIsoDateTimeSchema
});
const spaceDispatchTraceExecutionSchema = strictObject({
	phase: enumFactory([
		"starting",
		"accepted_local",
		"running",
		"completed_local",
		"result_received"
	]),
	phaseEnteredAt: nullableIsoDateTimeSchema,
	lastLifecycleAt: nullableIsoDateTimeSchema,
	progressEvidenceState: enumFactory([
		"active",
		"stalled",
		"unknown"
	]),
	timeoutPolicy: strictObject({
		finalizationTimeoutMs: boundedNullableNonNegativeIntegerSchema(SPACE_DISPATCH_TRACE_TIMEOUT_POLICY_MAX_MS),
		startTimeoutMs: boundedNullableNonNegativeIntegerSchema(SPACE_DISPATCH_TRACE_TIMEOUT_POLICY_MAX_MS),
		taskProgressEvidenceStaleAfterMs: boundedNullableNonNegativeIntegerSchema(SPACE_DISPATCH_TRACE_TIMEOUT_POLICY_MAX_MS),
		waitWindowMs: boundedNullableNonNegativeIntegerSchema(SPACE_DISPATCH_TRACE_TIMEOUT_POLICY_MAX_MS)
	})
});
const spaceDispatchTraceOriginSchema = strictObject({
	agentControllerConversationId: nullableTrimmedStringSchema,
	agentControllerRef: nullableTrimmedStringSchema,
	agentControllerConversationKind: nullableTrimmedStringSchema,
	resolvedAgentControllerConversationId: nullableTrimmedStringSchema,
	effectiveRuntimeWorkingDirectory: nullableTrimmedStringSchema,
	runtimeWorkspaceFingerprint: nullableTrimmedStringSchema
});
const spaceDispatchTracePromptSchema = strictObject({
	fingerprint: nullableTrimmedStringSchema,
	length: boundedNullableNonNegativeIntegerSchema(SPACE_DISPATCH_TRACE_PROMPT_LENGTH_MAX),
	preview: boundedNullableTrimmedStringSchema(SPACE_DISPATCH_TRACE_PROMPT_PREVIEW_MAX_CHARS)
});
const spaceDispatchTraceDispatchExecutionSchema = dispatchExecutionSchema;
const spaceDispatchTraceExecutionIdentitySchema = spaceTraceExecutionIdentityProjectionSchema;
const spaceDispatchTraceRuntimeCoordinatorExecutionSchema = runtimeCoordinatorExecutionRecordSchema.extend({ factSource: literal("runtime_coordinator_projection") });
const spaceDispatchTraceRuntimeCoordinatorCreationDiagnosticSchema = runtimeCoordinatorExecutionCreationDiagnosticSchema.extend({ factSource: literal("runtime_coordinator_projection") });
const spaceDispatchTraceRuntimeCoordinatorEventSchema = runtimeCoordinatorExecutionEventSchema.extend({ factSource: literal("runtime_coordinator_projection") });
const publishedRuntimeCoordinatorHeadLifecycleStatusSchema = enumFactory([
	"accepted",
	"execution_linked",
	"publication_intent_ready",
	"queued",
	"stopped_before_execution",
	"waiting"
]);
const publishedRuntimeCoordinatorHeadDecisionSourceSchema = enumFactory([
	"dispatch_preflight",
	"dispatch_delivery_pre_execution",
	"runtime_coordinator_head_waiting_deadline"
]);
const legacyPublishedRuntimeCoordinatorHeadPublicationCategorySchema = enumFactory([
	"binding_required",
	"local_connection_mismatch",
	"service_not_connected",
	"update_required"
]);
const legacyPublishedRuntimeCoordinatorHeadReasonCodeSchema = enumFactory([
	"agent_profile_binding.missing",
	"agent_profile_binding.disabled",
	"agent_profile_binding.owner_mismatch",
	"organization_binding_authorization.required",
	"local_service.identity_mismatch",
	"daemon.profile.offline",
	"service.update_required"
]);
const legacyPublishedRuntimeCoordinatorHeadRecoveryActionSchema = enumFactory([
	"set_up_agent_binding",
	"enable_agent_binding",
	"repair_agent_binding",
	"authorize_agent_binding_for_organization",
	"reconnect_agent_binding",
	"start_or_restart_openmeld_service",
	"update_openmeld_service"
]);
const publishedRuntimeCoordinatorHeadPublicationCategoryV2Schema = enumFactory([
	"binding_required",
	"local_connection_mismatch",
	"service_not_connected",
	"update_required",
	"wake_route_registration_pending"
]);
const publishedRuntimeCoordinatorHeadReasonCodeV2Schema = enumFactory([
	"agent_profile_binding.missing",
	"agent_profile_binding.disabled",
	"agent_profile_binding.owner_mismatch",
	"organization_binding_authorization.required",
	"local_service.identity_mismatch",
	"daemon.profile.offline",
	"daemon.profile.pending_first_report",
	"service.update_required"
]);
const publishedRuntimeCoordinatorHeadRecoveryActionV2Schema = enumFactory([
	"set_up_agent_binding",
	"enable_agent_binding",
	"repair_agent_binding",
	"authorize_agent_binding_for_organization",
	"reconnect_agent_binding",
	"start_or_restart_openmeld_service",
	"update_openmeld_service",
	"wait_for_wake_route_report"
]);
const currentPublishedRuntimeCoordinatorHeadPublicationCategorySchema = enumFactory([
	"space_access_unavailable",
	"binding_required",
	"agent_profile_unavailable",
	"local_agent_unavailable",
	"local_connection_mismatch",
	"provider_access_unavailable",
	"provider_usage_unavailable",
	"service_not_connected",
	"update_required",
	"wake_route_registration_pending",
	"workspace_unavailable"
]);
const currentPublishedRuntimeCoordinatorHeadReasonCodeSchema = string().trim().min(1).max(160);
const currentPublishedRuntimeCoordinatorHeadRecoveryActionSchema = enumFactory([
	"send_new_wake",
	"set_up_agent_binding",
	"enable_agent_binding",
	"repair_agent_binding",
	"authorize_agent_binding_for_organization",
	"check_status",
	"connect_local_agent",
	"enable_local_agent",
	"reconnect_agent_binding",
	"review_agent_settings",
	"start_or_restart_openmeld_service",
	"update_openmeld_service",
	"wait_for_usage_report",
	"wait_for_usage_reset",
	"wait_for_wake_route_report"
]);
const spaceDispatchTraceRuntimeCoordinatorHeadNoExecutionDecisionV1Schema = strictObject({
	decidedAt: ISO_DATE_TIME_SCHEMA,
	publicationCategory: legacyPublishedRuntimeCoordinatorHeadPublicationCategorySchema,
	reasonCode: legacyPublishedRuntimeCoordinatorHeadReasonCodeSchema,
	source: publishedRuntimeCoordinatorHeadDecisionSourceSchema,
	userRecoveryAction: legacyPublishedRuntimeCoordinatorHeadRecoveryActionSchema
});
const spaceDispatchTraceRuntimeCoordinatorHeadNoExecutionDecisionV2Schema = strictObject({
	decidedAt: ISO_DATE_TIME_SCHEMA,
	publicationCategory: publishedRuntimeCoordinatorHeadPublicationCategoryV2Schema,
	reasonCode: publishedRuntimeCoordinatorHeadReasonCodeV2Schema,
	source: publishedRuntimeCoordinatorHeadDecisionSourceSchema,
	userRecoveryAction: publishedRuntimeCoordinatorHeadRecoveryActionV2Schema
});
const spaceDispatchTraceRuntimeCoordinatorHeadNoExecutionDecisionSchema = strictObject({
	decidedAt: ISO_DATE_TIME_SCHEMA,
	publicationCategory: currentPublishedRuntimeCoordinatorHeadPublicationCategorySchema,
	reasonCode: currentPublishedRuntimeCoordinatorHeadReasonCodeSchema,
	source: publishedRuntimeCoordinatorHeadDecisionSourceSchema,
	userRecoveryAction: currentPublishedRuntimeCoordinatorHeadRecoveryActionSchema
});
const spaceDispatchTraceRuntimeCoordinatorHeadPublicationIntentV1Schema = union([strictObject({
	intentId: trimmedNonEmptyStringSchema.max(4096),
	kind: literal("no_execution_failure"),
	publicationCategory: legacyPublishedRuntimeCoordinatorHeadPublicationCategorySchema,
	readyAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: legacyPublishedRuntimeCoordinatorHeadReasonCodeSchema,
	status: literal("ready_for_phase_6"),
	userRecoveryAction: legacyPublishedRuntimeCoordinatorHeadRecoveryActionSchema
}), strictObject({
	intentId: trimmedNonEmptyStringSchema.max(4096),
	kind: literal("stopped_notice"),
	readyAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: literal("runtime_coordinator.pre_agent_stop_requested"),
	status: literal("ready_for_phase_6")
})]);
const spaceDispatchTraceRuntimeCoordinatorHeadPublicationIntentV2Schema = union([strictObject({
	intentId: trimmedNonEmptyStringSchema.max(4096),
	kind: literal("no_execution_failure"),
	publicationCategory: publishedRuntimeCoordinatorHeadPublicationCategoryV2Schema,
	readyAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: publishedRuntimeCoordinatorHeadReasonCodeV2Schema,
	status: literal("ready_for_phase_6"),
	userRecoveryAction: publishedRuntimeCoordinatorHeadRecoveryActionV2Schema
}), strictObject({
	intentId: trimmedNonEmptyStringSchema.max(4096),
	kind: literal("stopped_notice"),
	readyAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: literal("runtime_coordinator.pre_agent_stop_requested"),
	status: literal("ready_for_phase_6")
})]);
const spaceDispatchTraceRuntimeCoordinatorHeadPublicationIntentSchema = union([strictObject({
	intentId: trimmedNonEmptyStringSchema.max(4096),
	kind: literal("no_execution_failure"),
	publicationCategory: currentPublishedRuntimeCoordinatorHeadPublicationCategorySchema,
	readyAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: currentPublishedRuntimeCoordinatorHeadReasonCodeSchema,
	status: literal("ready_for_phase_6"),
	userRecoveryAction: currentPublishedRuntimeCoordinatorHeadRecoveryActionSchema
}), strictObject({
	intentId: trimmedNonEmptyStringSchema.max(4096),
	kind: literal("stopped_notice"),
	readyAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: literal("runtime_coordinator.pre_agent_stop_requested"),
	status: literal("ready_for_phase_6")
})]);
const spaceDispatchTraceRuntimeCoordinatorHeadPublicationOutcomeSchema = strictObject({
	failurePublicationCommandId: trimmedNonEmptyStringSchema.max(4096),
	finalMessageKind: enumFactory([
		"failure_notice",
		"status",
		"success_reply",
		"unknown"
	]).nullable(),
	observedAt: ISO_DATE_TIME_SCHEMA,
	publicationErrorCode: string().trim().min(1).max(160).nullable(),
	publishedSignalId: trimmedNonEmptyStringSchema.nullable(),
	status: enumFactory([
		"failure_published",
		"already_final",
		"failure_publication_failed"
	])
});
const spaceDispatchTraceRuntimeCoordinatorHeadPublicationExhaustionSchema = strictObject({
	attemptCount: int().positive(),
	exhaustedAt: ISO_DATE_TIME_SCHEMA,
	lastFailureAt: ISO_DATE_TIME_SCHEMA,
	lastReasonCode: string().trim().min(1).max(160),
	reasonCode: literal("runtime_coordinator.publication_retry_exhausted"),
	status: literal("publication_exhausted")
});
const spaceDispatchTraceRuntimeCoordinatorHeadTargetDisplaySchema = strictObject({
	targetProfileMentionLabel: spaceMentionLabelSchema.nullable(),
	targetProfileName: string().min(1).max(64).nullable()
});
const spaceDispatchTraceRuntimeCoordinatorHeadOfflineWaitingBoundarySchema = strictObject({
	deadlineAt: ISO_DATE_TIME_SCHEMA,
	deadlineOutcome: literal("publication_intent_ready"),
	observedAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: literal("daemon.profile.offline"),
	softDeadlineAt: ISO_DATE_TIME_SCHEMA,
	trigger: literal("openmeld_service_route_connected"),
	triggerOwner: literal("runtime_coordinator"),
	userRecoveryAction: literal("start_or_restart_openmeld_service"),
	userVisibleState: literal("waiting_for_openmeld_service"),
	waitingCategory: literal("service_not_connected"),
	waitingFor: literal("openmeld_service_route_connection"),
	waitingSince: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceRuntimeCoordinatorHeadPendingRouteWaitingBoundarySchema = strictObject({
	deadlineAt: ISO_DATE_TIME_SCHEMA,
	deadlineOutcome: literal("publication_intent_ready"),
	observedAt: ISO_DATE_TIME_SCHEMA,
	reasonCode: literal("daemon.profile.pending_first_report"),
	softDeadlineAt: ISO_DATE_TIME_SCHEMA,
	trigger: literal("wake_route_reported"),
	triggerOwner: literal("runtime_coordinator"),
	userRecoveryAction: literal("wait_for_wake_route_report"),
	userVisibleState: literal("waiting_for_wake_route_report"),
	waitingCategory: literal("wake_route_registration_pending"),
	waitingFor: literal("wake_route_report"),
	waitingSince: ISO_DATE_TIME_SCHEMA
});
const spaceDispatchTraceRuntimeCoordinatorHeadWaitingBoundarySchema = discriminatedUnion("reasonCode", [
	strictObject({
		deadlineAt: ISO_DATE_TIME_SCHEMA,
		deadlineOutcome: literal("publication_intent_ready"),
		observedAt: ISO_DATE_TIME_SCHEMA,
		reasonCode: literal("wake.device_wait.access_check_unavailable"),
		softDeadlineAt: ISO_DATE_TIME_SCHEMA,
		trigger: literal("space_access_rechecked"),
		triggerOwner: literal("runtime_coordinator"),
		userRecoveryAction: literal("check_status"),
		userVisibleState: literal("waiting_for_space_access"),
		waitingCategory: literal("space_access_unavailable"),
		waitingFor: literal("space_access_check"),
		waitingSince: ISO_DATE_TIME_SCHEMA
	}),
	strictObject({
		deadlineAt: ISO_DATE_TIME_SCHEMA,
		deadlineOutcome: literal("publication_intent_ready"),
		observedAt: ISO_DATE_TIME_SCHEMA,
		reasonCode: enumFactory(["wake.device_wait.readiness_checking", "daemon.service.draining"]),
		softDeadlineAt: ISO_DATE_TIME_SCHEMA,
		trigger: literal("agent_readiness_reported"),
		triggerOwner: literal("runtime_coordinator"),
		userRecoveryAction: literal("check_status"),
		userVisibleState: literal("waiting_for_agent_readiness"),
		waitingCategory: literal("local_agent_unavailable"),
		waitingFor: literal("agent_readiness_report"),
		waitingSince: ISO_DATE_TIME_SCHEMA
	}),
	spaceDispatchTraceRuntimeCoordinatorHeadOfflineWaitingBoundarySchema,
	spaceDispatchTraceRuntimeCoordinatorHeadPendingRouteWaitingBoundarySchema
]);
const spaceDispatchTraceRuntimeCoordinatorHeadFoundCommonShape = {
	createdAt: ISO_DATE_TIME_SCHEMA,
	duplicateDispatchCount: int().min(0),
	executionLinkedAt: nullableIsoDateTimeSchema,
	factSource: literal("runtime_coordinator_head_lifecycle"),
	modelVersion: literal("openmeld.runtime_coordinator.head_lifecycle.v1"),
	ownerUserId: trimmedNonEmptyStringSchema,
	primaryDispatchId: trimmedNonEmptyStringSchema,
	publicationExhaustion: spaceDispatchTraceRuntimeCoordinatorHeadPublicationExhaustionSchema.optional(),
	publicationOutcome: spaceDispatchTraceRuntimeCoordinatorHeadPublicationOutcomeSchema.optional(),
	readStatus: literal("found"),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.nullable(),
	status: publishedRuntimeCoordinatorHeadLifecycleStatusSchema,
	targetWakeLifecycleIdentity: targetWakeLifecycleIdentitySchema,
	targetWakeLifecycleId: trimmedNonEmptyStringSchema.max(2048),
	targetDisplay: spaceDispatchTraceRuntimeCoordinatorHeadTargetDisplaySchema.optional(),
	updatedAt: ISO_DATE_TIME_SCHEMA
};
const spaceDispatchTraceRuntimeCoordinatorHeadMissingSchema = strictObject({
	factSource: literal("runtime_coordinator_head_lifecycle"),
	readStatus: literal("missing"),
	reasonCode: literal("runtime_coordinator.head_lifecycle_not_found"),
	targetWakeLifecycleId: trimmedNonEmptyStringSchema.max(2048)
});
const spaceDispatchTraceRuntimeCoordinatorHeadAuthorityUnavailableSchema = strictObject({
	factSource: literal("runtime_coordinator_head_lifecycle"),
	readStatus: literal("authority_unavailable"),
	reasonCode: trimmedNonEmptyStringSchema.max(160),
	targetWakeLifecycleId: trimmedNonEmptyStringSchema.max(2048)
});
const spaceDispatchTraceRuntimeCoordinatorHeadLifecycleV1Schema = discriminatedUnion("readStatus", [
	strictObject({
		...spaceDispatchTraceRuntimeCoordinatorHeadFoundCommonShape,
		noExecutionDecision: union([spaceDispatchTraceRuntimeCoordinatorHeadNoExecutionDecisionV1Schema, nullFactory()]),
		publicationIntent: union([spaceDispatchTraceRuntimeCoordinatorHeadPublicationIntentV1Schema, nullFactory()]),
		waitingBoundary: union([spaceDispatchTraceRuntimeCoordinatorHeadOfflineWaitingBoundarySchema, nullFactory()])
	}),
	spaceDispatchTraceRuntimeCoordinatorHeadMissingSchema,
	spaceDispatchTraceRuntimeCoordinatorHeadAuthorityUnavailableSchema
]);
const spaceDispatchTraceRuntimeCoordinatorHeadLifecycleV2Schema = discriminatedUnion("readStatus", [
	strictObject({
		...spaceDispatchTraceRuntimeCoordinatorHeadFoundCommonShape,
		noExecutionDecision: union([spaceDispatchTraceRuntimeCoordinatorHeadNoExecutionDecisionV2Schema, nullFactory()]),
		publicationIntent: union([spaceDispatchTraceRuntimeCoordinatorHeadPublicationIntentV2Schema, nullFactory()]),
		waitingBoundary: union([spaceDispatchTraceRuntimeCoordinatorHeadWaitingBoundarySchema, nullFactory()])
	}),
	spaceDispatchTraceRuntimeCoordinatorHeadMissingSchema,
	spaceDispatchTraceRuntimeCoordinatorHeadAuthorityUnavailableSchema
]);
const spaceDispatchTraceRuntimeCoordinatorHeadLifecycleSchema = discriminatedUnion("readStatus", [
	strictObject({
		...spaceDispatchTraceRuntimeCoordinatorHeadFoundCommonShape,
		noExecutionDecision: union([spaceDispatchTraceRuntimeCoordinatorHeadNoExecutionDecisionSchema, nullFactory()]),
		publicationIntent: union([spaceDispatchTraceRuntimeCoordinatorHeadPublicationIntentSchema, nullFactory()]),
		waitingBoundary: union([spaceDispatchTraceRuntimeCoordinatorHeadWaitingBoundarySchema, nullFactory()])
	}),
	spaceDispatchTraceRuntimeCoordinatorHeadMissingSchema,
	spaceDispatchTraceRuntimeCoordinatorHeadAuthorityUnavailableSchema
]);
const spaceDispatchTraceStructuredActionFailureSchema = strictObject({
	carrierKind: enumFactory([
		"fenced_block",
		"invalid_json",
		"none",
		"trailing_json"
	]),
	invalidOutputPreview: boundedNullableTrimmedStringSchema(SPACE_DISPATCH_TRACE_INVALID_OUTPUT_PREVIEW_MAX_CHARS),
	publicFailureReason: nullableTrimmedStringSchema,
	repairMode: enumFactory([
		"required_check",
		"local_synthetic",
		"controller_roundtrip"
	]),
	spaceActionCandidatePresent: boolean(),
	spaceActionParseError: nullableTrimmedStringSchema
});
const spaceDispatchTraceOpenClawGatewayVisibilitySchema = strictObject({
	agentId: trimmedNonEmptyStringSchema,
	gatewayRestartAttempted: boolean(),
	gatewayVisibility: enumFactory([
		"missing_after_restart",
		"missing_before_restart",
		"visible",
		"visible_after_restart"
	])
});
const spaceDispatchTraceOpenClawBindingSchema = strictObject({
	classification: enumFactory([
		"missing",
		"canonical_owned",
		"legacy",
		"foreign"
	]),
	bindingSource: union([enumFactory(["daemon-launch", "imported"]), nullFactory()]),
	upgradeApplied: boolean()
});
const spaceDispatchTraceSnapshotSchema = strictObject({
	dispatch: spaceDispatchTraceDispatchSchema,
	nativeDelivery: union([spaceDispatchTraceNativeDeliverySchema, nullFactory()]).optional(),
	runtimeCoordinatorExecution: union([spaceDispatchTraceRuntimeCoordinatorExecutionSchema, nullFactory()]),
	runtimeCoordinatorCreationDiagnostics: array(spaceDispatchTraceRuntimeCoordinatorCreationDiagnosticSchema),
	runtimeCoordinatorEvents: array(spaceDispatchTraceRuntimeCoordinatorEventSchema),
	runtimeCoordinatorHeadLifecycle: union([spaceDispatchTraceRuntimeCoordinatorHeadLifecycleSchema, nullFactory()]),
	latestAttempt: union([spaceDispatchTraceLatestAttemptSchema, nullFactory()]),
	executionIdentity: union([spaceDispatchTraceExecutionIdentitySchema, nullFactory()]),
	claimLease: union([spaceDispatchTraceClaimLeaseSchema, nullFactory()]),
	result: union([spaceDispatchTraceResultSchema, nullFactory()]),
	resultInbox: union([spaceDispatchTraceResultInboxSchema, nullFactory()]),
	recentOpsDecisions: array(spaceDispatchTraceOpsDecisionSchema),
	replySignal: union([spaceDispatchTraceReplySignalSchema, nullFactory()]),
	finalVisibleMessage: union([spaceDispatchTraceFinalVisibleMessageSchema, nullFactory()]).optional(),
	finalVisibleMessageLinkage: union([spaceDispatchTraceFinalVisibleMessageLinkageSchema, nullFactory()]).optional(),
	finalVisibleOutcome: spaceDispatchTraceFinalVisibleOutcomeSchema.optional(),
	streaming: union([spaceDispatchTraceStreamingSchema, nullFactory()]),
	runtime: union([spaceDispatchTraceRuntimeSchema, nullFactory()]),
	execution: union([spaceDispatchTraceExecutionSchema, nullFactory()]),
	origin: union([spaceDispatchTraceOriginSchema, nullFactory()]),
	prompt: union([spaceDispatchTracePromptSchema, nullFactory()]),
	dispatchExecution: union([spaceDispatchTraceDispatchExecutionSchema, nullFactory()]),
	openClawGatewayVisibility: union([spaceDispatchTraceOpenClawGatewayVisibilitySchema, nullFactory()]),
	openClawBinding: union([spaceDispatchTraceOpenClawBindingSchema, nullFactory()]),
	structuredActionFailure: union([spaceDispatchTraceStructuredActionFailureSchema, nullFactory()])
});
const spaceDispatchTraceSnapshotV1Schema = spaceDispatchTraceSnapshotSchema.extend({ runtimeCoordinatorHeadLifecycle: union([spaceDispatchTraceRuntimeCoordinatorHeadLifecycleV1Schema, nullFactory()]) });
const spaceDispatchTraceSnapshotV2Schema = spaceDispatchTraceSnapshotSchema.extend({ runtimeCoordinatorHeadLifecycle: union([spaceDispatchTraceRuntimeCoordinatorHeadLifecycleV2Schema, nullFactory()]) });
const spaceDispatchTraceResponseSchema = strictObject({
	correlation: observabilityWakeCorrelationSchema,
	query: spaceDispatchTraceQuerySchema,
	serviceConnectionDiagnostics: serviceConnectionTraceDiagnosticsSchema.optional(),
	remoteAgentRuntimeDiagnostics: remoteAgentWorkerRuntimeDiagnosticsResponseSchema.optional(),
	trace: union([spaceDispatchTraceSnapshotSchema, nullFactory()])
}).superRefine((value, context) => {
	expectTraceEvidence(context, value.correlation.selector.kind, "dispatch", [
		"correlation",
		"selector",
		"kind"
	], "trace response correlation selector must be dispatch");
	expectTraceEvidence(context, value.correlation.spaceId, value.query.spaceId, ["correlation", "spaceId"], "correlation.spaceId must match query.spaceId");
	expectTraceEvidence(context, value.correlation.dispatchId, value.query.dispatchId, ["correlation", "dispatchId"], "correlation.dispatchId must match query.dispatchId");
	if (value.trace === null) {
		expectNullTraceEvidence(context, value.correlation.clientMessageId, ["correlation", "clientMessageId"]);
		expectNullTraceEvidence(context, value.correlation.sourceSignalId, ["correlation", "sourceSignalId"]);
		expectNullTraceEvidence(context, value.correlation.targetProfileId, ["correlation", "targetProfileId"]);
		expectNullTraceEvidence(context, value.correlation.latestAttemptId, ["correlation", "latestAttemptId"]);
		expectNullTraceEvidence(context, value.correlation.runtimeCoordinatorExecutionId, ["correlation", "runtimeCoordinatorExecutionId"]);
		expectNullTraceEvidence(context, value.correlation.claimLeaseId, ["correlation", "claimLeaseId"]);
		expectNullTraceEvidence(context, value.correlation.bindingId, ["correlation", "bindingId"]);
		expectNullTraceEvidence(context, value.correlation.openMeldServiceRuntimeId, ["correlation", "openMeldServiceRuntimeId"]);
		expectNullTraceEvidence(context, value.correlation.agentControllerRef, ["correlation", "agentControllerRef"]);
		expectNullTraceEvidence(context, value.correlation.resultRef, ["correlation", "resultRef"]);
		expectNullTraceEvidence(context, value.correlation.publishedSignalId, ["correlation", "publishedSignalId"]);
		expectNullTraceEvidence(context, value.correlation.replySignalId, ["correlation", "replySignalId"]);
		return;
	}
	expectTraceEvidence(context, value.trace.dispatch.dispatchId, value.query.dispatchId, [
		"trace",
		"dispatch",
		"dispatchId"
	], "trace.dispatch.dispatchId must match query.dispatchId");
	expectTraceEvidence(context, value.correlation.clientMessageId, value.trace.dispatch.sourceClientMessageId, ["correlation", "clientMessageId"], "correlation.clientMessageId must match trace.dispatch.sourceClientMessageId");
	expectTraceEvidence(context, value.correlation.sourceSignalId, value.trace.dispatch.sourceEventId, ["correlation", "sourceSignalId"], "correlation.sourceSignalId must match trace.dispatch.sourceEventId");
	expectTraceEvidence(context, value.correlation.targetProfileId, value.trace.dispatch.targetProfileId, ["correlation", "targetProfileId"], "correlation.targetProfileId must match trace.dispatch.targetProfileId");
	expectTraceEvidence(context, value.correlation.latestAttemptId, value.trace.latestAttempt?.attemptId ?? value.trace.dispatch.latestAttemptId, ["correlation", "latestAttemptId"], "correlation.latestAttemptId must match latest attempt evidence");
	expectTraceEvidence(context, value.correlation.runtimeCoordinatorExecutionId, value.trace.runtimeCoordinatorExecution?.runtimeCoordinatorExecutionId ?? null, ["correlation", "runtimeCoordinatorExecutionId"], "correlation.runtimeCoordinatorExecutionId must match trace.runtimeCoordinatorExecution.runtimeCoordinatorExecutionId");
	expectTraceEvidence(context, value.correlation.claimLeaseId, value.trace.claimLease?.claimLeaseId ?? null, ["correlation", "claimLeaseId"], "correlation.claimLeaseId must match trace.claimLease.claimLeaseId");
	expectTraceEvidence(context, value.correlation.bindingId, value.trace.runtimeCoordinatorExecution?.bindingId ?? value.trace.executionIdentity?.core.bindingId ?? value.trace.nativeDelivery?.bindingId ?? value.trace.claimLease?.bindingId ?? null, ["correlation", "bindingId"], "correlation.bindingId must match trace execution identity evidence");
	expectTraceEvidence(context, value.correlation.openMeldServiceRuntimeId, value.trace.runtimeCoordinatorExecution?.openMeldServiceRuntimeId.value ?? value.trace.executionIdentity?.core.openMeldServiceRuntimeId.value ?? value.trace.claimLease?.openMeldServiceRuntimeId ?? null, ["correlation", "openMeldServiceRuntimeId"], "correlation.openMeldServiceRuntimeId must match trace execution identity evidence");
	expectTraceEvidence(context, value.correlation.agentControllerRef, value.trace.runtimeCoordinatorExecution?.agentControllerRef ?? value.trace.executionIdentity?.core.agentControllerRef ?? value.trace.nativeDelivery?.controllerNormalization.canonicalAgentControllerRef ?? value.trace.claimLease?.agentControllerRef ?? null, ["correlation", "agentControllerRef"], "correlation.agentControllerRef must match trace execution identity evidence");
	expectTraceEvidence(context, value.correlation.resultRef, value.trace.result?.resultRef ?? null, ["correlation", "resultRef"], "correlation.resultRef must match trace.result.resultRef");
	expectTraceEvidence(context, value.correlation.publishedSignalId, value.trace.result?.publishedSignalId ?? null, ["correlation", "publishedSignalId"], "correlation.publishedSignalId must match trace.result.publishedSignalId");
	expectTraceEvidence(context, value.correlation.replySignalId, value.trace.replySignal?.signalId ?? null, ["correlation", "replySignalId"], "correlation.replySignalId must match trace.replySignal.signalId");
	expectFinalVisibleMessageTraceEvidence(context, value.trace);
	if (value.trace.latestAttempt) expectTraceEvidence(context, value.trace.latestAttempt.dispatchId, value.trace.dispatch.dispatchId, [
		"trace",
		"latestAttempt",
		"dispatchId"
	], "trace.latestAttempt.dispatchId must match trace.dispatch.dispatchId");
	expectNativeDeliveryTraceEvidence(context, {
		query: value.query,
		trace: value.trace
	});
	if (value.trace.claimLease) {
		expectTraceEvidence(context, value.trace.claimLease.dispatchId, value.trace.dispatch.dispatchId, [
			"trace",
			"claimLease",
			"dispatchId"
		], "trace.claimLease.dispatchId must match trace.dispatch.dispatchId");
		expectTraceEvidence(context, value.trace.claimLease.sourceEventId, value.trace.dispatch.sourceEventId, [
			"trace",
			"claimLease",
			"sourceEventId"
		], "trace.claimLease.sourceEventId must match trace.dispatch.sourceEventId");
		expectTraceEvidence(context, value.trace.claimLease.sourceClientMessageId, value.trace.dispatch.sourceClientMessageId, [
			"trace",
			"claimLease",
			"sourceClientMessageId"
		], "trace.claimLease.sourceClientMessageId must match trace.dispatch.sourceClientMessageId");
		expectTraceEvidence(context, value.trace.claimLease.targetProfileId, value.trace.dispatch.targetProfileId, [
			"trace",
			"claimLease",
			"targetProfileId"
		], "trace.claimLease.targetProfileId must match trace.dispatch.targetProfileId");
	}
	if (value.trace.executionIdentity) {
		expectTraceEvidence(context, value.trace.executionIdentity.core.spaceId, value.query.spaceId, [
			"trace",
			"executionIdentity",
			"core",
			"spaceId"
		], "trace.executionIdentity.core.spaceId must match query.spaceId");
		expectTraceEvidence(context, value.trace.executionIdentity.core.dispatchId, value.trace.dispatch.dispatchId, [
			"trace",
			"executionIdentity",
			"core",
			"dispatchId"
		], "trace.executionIdentity.core.dispatchId must match trace.dispatch.dispatchId");
		expectTraceEvidence(context, value.trace.executionIdentity.core.targetProfileId, value.trace.dispatch.targetProfileId, [
			"trace",
			"executionIdentity",
			"core",
			"targetProfileId"
		], "trace.executionIdentity.core.targetProfileId must match trace.dispatch.targetProfileId");
	}
	if (value.trace.runtimeCoordinatorExecution) {
		expectTraceEvidence(context, value.trace.runtimeCoordinatorExecution.spaceId, value.query.spaceId, [
			"trace",
			"runtimeCoordinatorExecution",
			"spaceId"
		], "trace.runtimeCoordinatorExecution.spaceId must match query.spaceId");
		expectTraceEvidence(context, value.trace.runtimeCoordinatorExecution.dispatchId, value.trace.dispatch.dispatchId, [
			"trace",
			"runtimeCoordinatorExecution",
			"dispatchId"
		], "trace.runtimeCoordinatorExecution.dispatchId must match trace.dispatch.dispatchId");
		expectTraceEvidence(context, value.trace.runtimeCoordinatorExecution.targetProfileId, value.trace.dispatch.targetProfileId, [
			"trace",
			"runtimeCoordinatorExecution",
			"targetProfileId"
		], "trace.runtimeCoordinatorExecution.targetProfileId must match trace.dispatch.targetProfileId");
	}
	expectRuntimeCoordinatorHeadLifecycleTraceEvidence(context, {
		query: value.query,
		trace: value.trace
	});
	for (const diagnostic of value.trace.runtimeCoordinatorCreationDiagnostics) {
		expectTraceEvidence(context, diagnostic.spaceId, value.query.spaceId, ["trace", "runtimeCoordinatorCreationDiagnostics"], "trace.runtimeCoordinatorCreationDiagnostics.spaceId must match query.spaceId");
		expectTraceEvidence(context, diagnostic.dispatchId, value.trace.dispatch.dispatchId, ["trace", "runtimeCoordinatorCreationDiagnostics"], "trace.runtimeCoordinatorCreationDiagnostics.dispatchId must match trace.dispatch.dispatchId");
		expectTraceEvidence(context, diagnostic.targetProfileId, value.trace.dispatch.targetProfileId, ["trace", "runtimeCoordinatorCreationDiagnostics"], "trace.runtimeCoordinatorCreationDiagnostics.targetProfileId must match trace.dispatch.targetProfileId");
	}
	for (const event of value.trace.runtimeCoordinatorEvents) expectTraceEvidence(context, event.runtimeCoordinatorExecutionId, value.trace.runtimeCoordinatorExecution?.runtimeCoordinatorExecutionId ?? null, ["trace", "runtimeCoordinatorEvents"], "trace.runtimeCoordinatorEvents.runtimeCoordinatorExecutionId must match trace.runtimeCoordinatorExecution.runtimeCoordinatorExecutionId");
	if (value.trace.result) expectTraceEvidence(context, value.trace.result.dispatchId, value.trace.dispatch.dispatchId, [
		"trace",
		"result",
		"dispatchId"
	], "trace.result.dispatchId must match trace.dispatch.dispatchId");
	if (value.trace.resultInbox) {
		expectTraceEvidence(context, value.trace.resultInbox.dispatchId, value.trace.dispatch.dispatchId, [
			"trace",
			"resultInbox",
			"dispatchId"
		], "trace.resultInbox.dispatchId must match trace.dispatch.dispatchId");
		expectTraceEvidence(context, value.trace.resultInbox.targetProfileId, value.trace.dispatch.targetProfileId, [
			"trace",
			"resultInbox",
			"targetProfileId"
		], "trace.resultInbox.targetProfileId must match trace.dispatch.targetProfileId");
		expectTraceEvidence(context, value.trace.resultInbox.attemptId, value.trace.latestAttempt?.attemptId ?? value.trace.dispatch.latestAttemptId, [
			"trace",
			"resultInbox",
			"attemptId"
		], "trace.resultInbox.attemptId must match latest attempt evidence");
	}
});
const spaceDispatchTraceResponseV1Schema = spaceDispatchTraceResponseSchema.safeExtend({ trace: union([spaceDispatchTraceSnapshotV1Schema, nullFactory()]) });
const spaceDispatchTraceResponseV2Schema = spaceDispatchTraceResponseSchema.safeExtend({ trace: union([spaceDispatchTraceSnapshotV2Schema, nullFactory()]) });
const FINAL_VISIBLE_OUTCOME_PUBLICATION_PENDING_REASON_CODE = "final_visible_outcome.publication_pending";
const FINAL_VISIBLE_OUTCOME_PUBLICATION_RETRYING_REASON_CODE = "final_visible_outcome.publication_retrying";
const FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE = "final_visible_outcome.publication_unresolved";
const RUNTIME_COORDINATOR_PUBLICATION_UNRESOLVED_REASON_CODE = "runtime_coordinator.publication_unresolved";
const RUNTIME_COORDINATOR_DELIVERY_EXPIRED_REASON_CODE = "runtime_coordinator.delivery_expired";
function listSpaceDispatchTraceFinalVisibleSignalCandidates(trace) {
	const candidates = [];
	addRuntimeCoordinatorStoppedNoticeSignalCandidates(candidates, trace.runtimeCoordinatorEvents);
	addFinalVisibleSignalCandidate(candidates, trace.runtimeCoordinatorHeadLifecycle?.readStatus === "found" ? trace.runtimeCoordinatorHeadLifecycle.publicationOutcome?.publishedSignalId : null);
	addFinalVisibleSignalCandidate(candidates, trace.result?.publishedSignalId);
	addFinalVisibleSignalCandidate(candidates, trace.resultInbox?.publishedSignalId);
	return candidates;
}
function addRuntimeCoordinatorStoppedNoticeSignalCandidates(candidates, events) {
	for (const event of events) {
		if (event.eventType !== "cancellation_control_accepted") continue;
		addFinalVisibleSignalCandidate(candidates, readPayloadString(readPayloadObject(parseRuntimeCoordinatorEventPayload(event), "stoppedNoticePublication"), "publishedSignalId"));
	}
}
function addFinalVisibleSignalCandidate(candidates, value) {
	const normalized = normalizeOptionalTraceText(value);
	if (normalized && !candidates.includes(normalized)) candidates.push(normalized);
}
function normalizeOptionalTraceText(value) {
	const normalized = typeof value === "string" ? value.trim() : "";
	return normalized.length > 0 ? normalized : null;
}
function projectSpaceDispatchTraceFinalVisibleOutcome(trace) {
	return resolveFinalVisibleMessageOutcome(trace) ?? resolvePublicationBlockerOutcome(trace) ?? resolveHeadPublicationExhaustionOutcome(trace) ?? resolveHeadPublicationOutcome(trace) ?? resolveResultPublicationOutcome(trace) ?? resolveRuntimeCoordinatorExecutionPublicationOutcome(trace) ?? resolveHeadPublicationIntentOutcome(trace) ?? createNotApplicableFinalVisibleOutcome();
}
function resolveFinalVisibleMessageOutcome(trace) {
	const finalVisibleMessage = trace.finalVisibleMessage ?? null;
	if (!finalVisibleMessage) return null;
	const visibleStopped = isStoppedFinalVisibleMessage(finalVisibleMessage);
	const visibleFailure = isFailureFinalVisibleSystemNotice(finalVisibleMessage) || isFailureFinalVisibleTextMessage(finalVisibleMessage);
	let publicationKind = "success_reply";
	let status = "visible_reply";
	if (visibleStopped) {
		publicationKind = "stopped_notice";
		status = "visible_stopped";
	} else if (visibleFailure) {
		publicationKind = "failure_notice";
		status = "visible_failure";
	}
	return {
		delivered: true,
		publicationErrorCode: null,
		publicationKind,
		reasonCode: resolveFinalVisibleMessageOutcomeReasonCode(finalVisibleMessage, visibleStopped, visibleFailure),
		signalId: finalVisibleMessage.signalId,
		sourceFact: "final_visible_message",
		sourceReasonCode: finalVisibleMessage.reasonCode,
		sourceStatus: finalVisibleMessage.signalType,
		status
	};
}
function isStoppedFinalVisibleMessage(finalVisibleMessage) {
	return finalVisibleMessage.signalType === "signal.system_notice" && finalVisibleMessage.noticeType === "wake_stopped" || finalVisibleMessage.signalType === "signal.text" && finalVisibleMessage.reasonCode === "remote_agent.execution_cancelled";
}
function isFailureFinalVisibleSystemNotice(finalVisibleMessage) {
	return finalVisibleMessage.signalType === "signal.system_notice" && (finalVisibleMessage.noticeType === "terminal_failure" || finalVisibleMessage.noticeType === "policy_blocked");
}
function isFailureFinalVisibleTextMessage(finalVisibleMessage) {
	return finalVisibleMessage.signalType === "signal.text" && finalVisibleMessage.reasonCode !== null;
}
function resolveFinalVisibleMessageOutcomeReasonCode(finalVisibleMessage, visibleStopped, visibleFailure) {
	if (visibleStopped) return finalVisibleMessage.reasonCode ?? "space.wake_stopped_notice_published";
	if (!visibleFailure) return finalVisibleMessage.noticeType === "stay_silent" ? "space.stay_silent_notice_published" : "reply.delivered";
	if (finalVisibleMessage.reasonCode) return finalVisibleMessage.reasonCode;
	return finalVisibleMessage.noticeType === "policy_blocked" ? "space.policy_blocked_notice_published" : "space.terminal_failure_notice_published";
}
function resolvePublicationBlockerOutcome(trace) {
	const publicationBlocker = resolveRuntimeCoordinatorPublicationUnresolvedBlocker(trace);
	return publicationBlocker ? {
		delivered: false,
		publicationErrorCode: null,
		publicationKind: publicationBlocker.publicationKind,
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
		signalId: null,
		sourceFact: "runtime_coordinator_event",
		sourceReasonCode: publicationBlocker.reasonCode,
		sourceStatus: publicationBlocker.status,
		status: "publication_unresolved"
	} : null;
}
function resolveHeadPublicationExhaustionOutcome(trace) {
	const headPublicationExhaustion = trace.runtimeCoordinatorHeadLifecycle?.readStatus === "found" ? trace.runtimeCoordinatorHeadLifecycle.publicationExhaustion ?? null : null;
	if (!headPublicationExhaustion) return null;
	return {
		delivered: false,
		publicationErrorCode: null,
		publicationKind: "failure_notice",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
		signalId: null,
		sourceFact: "runtime_coordinator_head_lifecycle",
		sourceReasonCode: headPublicationExhaustion.reasonCode,
		sourceStatus: headPublicationExhaustion.status,
		status: "publication_unresolved"
	};
}
function resolveHeadPublicationOutcome(trace) {
	const headPublicationOutcome = trace.runtimeCoordinatorHeadLifecycle?.readStatus === "found" ? trace.runtimeCoordinatorHeadLifecycle.publicationOutcome ?? null : null;
	if (!headPublicationOutcome) return null;
	if (headPublicationOutcome?.status === "failure_publication_failed") return {
		delivered: false,
		publicationErrorCode: headPublicationOutcome.publicationErrorCode,
		publicationKind: "failure_notice",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_RETRYING_REASON_CODE,
		signalId: headPublicationOutcome.publishedSignalId,
		sourceFact: "runtime_coordinator_head_lifecycle",
		sourceReasonCode: headPublicationOutcome.publicationErrorCode ?? "runtime_coordinator.failure_publication_failed",
		sourceStatus: headPublicationOutcome.status,
		status: "publication_retrying"
	};
	return {
		delivered: false,
		publicationErrorCode: headPublicationOutcome.publicationErrorCode,
		publicationKind: headPublicationOutcome.finalMessageKind === "success_reply" ? "success_reply" : "failure_notice",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
		signalId: headPublicationOutcome.publishedSignalId,
		sourceFact: "runtime_coordinator_head_lifecycle",
		sourceReasonCode: headPublicationOutcome.publicationErrorCode ?? "reply.not_found",
		sourceStatus: headPublicationOutcome.status,
		status: "publication_unresolved"
	};
}
function resolveResultPublicationOutcome(trace) {
	if (hasRuntimeCoordinatorPreResultAuthority(trace)) return null;
	const resultInbox = trace.resultInbox;
	if (resultInbox?.publishState === "reply_publication_failed") return {
		delivered: false,
		publicationErrorCode: resultInbox.publicationErrorCode,
		publicationKind: "success_reply",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_RETRYING_REASON_CODE,
		signalId: resultInbox.publishedSignalId,
		sourceFact: "result_inbox",
		sourceReasonCode: resultInbox.publicationErrorCode ?? "reply.publication_failed",
		sourceStatus: resultInbox.publishState,
		status: "publication_retrying"
	};
	if (resultInbox?.publishState === "reply_published" || trace.result?.publishedSignalId) return {
		delivered: false,
		publicationErrorCode: resultInbox?.publicationErrorCode ?? null,
		publicationKind: "success_reply",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
		signalId: trace.result?.publishedSignalId ?? resultInbox?.publishedSignalId ?? null,
		sourceFact: trace.result?.publishedSignalId ? "dispatch_result" : "result_inbox",
		sourceReasonCode: "reply.not_found",
		sourceStatus: resultInbox?.publishState ?? (trace.result?.publishedSignalId ? "published_signal_missing" : null),
		status: "publication_unresolved"
	};
	return null;
}
function resolveRuntimeCoordinatorExecutionPublicationOutcome(trace) {
	const runtimeCoordinatorExecution = trace.runtimeCoordinatorExecution;
	const runtimeCoordinatorReason = runtimeCoordinatorExecution?.terminalOutcome?.reasonCode ?? null;
	const publicationFailureEvent = trace.runtimeCoordinatorEvents.filter((event) => event.eventType === "reply_publication_failed" && event.runtimeCoordinatorExecutionId === runtimeCoordinatorExecution?.runtimeCoordinatorExecutionId).at(-1);
	if (runtimeCoordinatorExecution?.status === "failed_retryable" && publicationFailureEvent) {
		const publicationReason = publicationFailureEvent.reasonCode ?? runtimeCoordinatorReason;
		return {
			delivered: false,
			publicationErrorCode: publicationReason,
			publicationKind: "success_reply",
			reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_RETRYING_REASON_CODE,
			signalId: null,
			sourceFact: "runtime_coordinator_execution",
			sourceReasonCode: publicationReason,
			sourceStatus: runtimeCoordinatorExecution.status,
			status: "publication_retrying"
		};
	}
	if (runtimeCoordinatorExecution?.status === "failed_terminal" && runtimeCoordinatorReason === RUNTIME_COORDINATOR_DELIVERY_EXPIRED_REASON_CODE) return {
		delivered: false,
		publicationErrorCode: null,
		publicationKind: "failure_notice",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
		signalId: null,
		sourceFact: "runtime_coordinator_execution",
		sourceReasonCode: runtimeCoordinatorReason,
		sourceStatus: runtimeCoordinatorExecution.status,
		status: "publication_unresolved"
	};
	if (runtimeCoordinatorExecution?.status === "reply_published") return {
		delivered: false,
		publicationErrorCode: null,
		publicationKind: "success_reply",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
		signalId: null,
		sourceFact: "runtime_coordinator_execution",
		sourceReasonCode: "reply.not_found",
		sourceStatus: runtimeCoordinatorExecution.status,
		status: "publication_unresolved"
	};
	if (runtimeCoordinatorExecution?.status === "cancelled") return {
		delivered: false,
		publicationErrorCode: null,
		publicationKind: "stopped_notice",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
		signalId: null,
		sourceFact: "runtime_coordinator_execution",
		sourceReasonCode: runtimeCoordinatorReason ?? "space.wake_stopped_notice_missing",
		sourceStatus: runtimeCoordinatorExecution.status,
		status: "publication_unresolved"
	};
	return null;
}
function resolveHeadPublicationIntentOutcome(trace) {
	const headLifecycle = trace.runtimeCoordinatorHeadLifecycle;
	if (headLifecycle?.readStatus !== "found" || !headLifecycle.publicationIntent) return null;
	return {
		delivered: false,
		publicationErrorCode: null,
		publicationKind: headLifecycle.publicationIntent.kind === "stopped_notice" ? "stopped_notice" : "failure_notice",
		reasonCode: FINAL_VISIBLE_OUTCOME_PUBLICATION_PENDING_REASON_CODE,
		signalId: null,
		sourceFact: "runtime_coordinator_head_lifecycle",
		sourceReasonCode: headLifecycle.publicationIntent.reasonCode,
		sourceStatus: headLifecycle.publicationIntent.status,
		status: "publication_pending"
	};
}
function createNotApplicableFinalVisibleOutcome() {
	return {
		delivered: false,
		publicationErrorCode: null,
		publicationKind: null,
		reasonCode: null,
		signalId: null,
		sourceFact: "none",
		sourceReasonCode: null,
		sourceStatus: null,
		status: "not_applicable"
	};
}
function projectSpaceDispatchTraceRootCause(trace) {
	const finalVisibleOutcomeRootCause = resolveFinalVisibleOutcomeRootCause(trace);
	if (finalVisibleOutcomeRootCause) return finalVisibleOutcomeRootCause;
	const legacyResultInboxRootCause = resolveLegacyResultInboxRootCause(trace);
	if (legacyResultInboxRootCause) return legacyResultInboxRootCause;
	const headLifecycleRootCause = resolveRuntimeCoordinatorHeadLifecycleRootCause(trace);
	if (headLifecycleRootCause) return headLifecycleRootCause;
	const runtimeCoordinatorCreationFailure = resolveRuntimeCoordinatorCreationRootCause(trace);
	if (runtimeCoordinatorCreationFailure) return runtimeCoordinatorCreationFailure;
	const runtimeProgressStale = resolveRuntimeProgressStaleRootCause(trace);
	if (runtimeProgressStale) return runtimeProgressStale;
	const runtimeCoordinatorRootCause = resolveRuntimeCoordinatorExecutionRootCause(trace);
	if (runtimeCoordinatorRootCause) return runtimeCoordinatorRootCause;
	const attemptErrorRootCause = resolveLatestAttemptErrorRootCause(trace);
	if (attemptErrorRootCause) return attemptErrorRootCause;
	if (trace.result?.publishedSignalId) return {
		reasonCode: "reply.not_found",
		source: "reply_lookup"
	};
	if (trace.dispatch.status === "succeeded") return {
		reasonCode: "dispatch.succeeded",
		source: "dispatch"
	};
	if (trace.execution?.progressEvidenceState === "stalled") return {
		reasonCode: "daemon.execution_stalled",
		source: "runtime"
	};
	if (trace.runtime?.timeoutTriggeredAt) return {
		reasonCode: "runtime.timeout",
		source: "runtime"
	};
	if (trace.latestAttempt?.status) return {
		reasonCode: `attempt.${trace.latestAttempt.status}`,
		source: "latest_attempt"
	};
	return {
		reasonCode: `dispatch.${trace.dispatch.status}`,
		source: "dispatch"
	};
}
function resolveSpaceDispatchTraceRootCause(trace) {
	return projectSpaceDispatchTraceRootCause(trace).reasonCode;
}
function resolveFinalVisibleOutcomeRootCause(trace) {
	const finalVisibleOutcome = projectSpaceDispatchTraceFinalVisibleOutcome(trace);
	switch (finalVisibleOutcome.status) {
		case "visible_reply": return {
			reasonCode: finalVisibleOutcome.reasonCode ?? "reply.delivered",
			source: "final_visible_message"
		};
		case "visible_failure": return {
			reasonCode: finalVisibleOutcome.reasonCode ?? "space.terminal_failure_notice_published",
			source: "final_visible_message"
		};
		case "visible_stopped": return {
			reasonCode: finalVisibleOutcome.reasonCode ?? "space.wake_stopped_notice_published",
			source: "final_visible_message"
		};
		case "publication_pending":
		case "publication_retrying": return {
			reasonCode: finalVisibleOutcome.reasonCode ?? FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
			source: "final_visible_outcome"
		};
		case "publication_unresolved": return resolveFinalVisibleMessageLinkageRootCause(trace, finalVisibleOutcome) ?? {
			reasonCode: finalVisibleOutcome.reasonCode ?? FINAL_VISIBLE_OUTCOME_PUBLICATION_UNRESOLVED_REASON_CODE,
			source: "final_visible_outcome"
		};
		case "not_applicable": return resolveFinalVisibleMessageLinkageRootCause(trace, finalVisibleOutcome);
		default: return assertNever(finalVisibleOutcome.status);
	}
}
function resolveFinalVisibleMessageLinkageRootCause(trace, finalVisibleOutcome) {
	const linkage = trace.finalVisibleMessageLinkage ?? null;
	if (!linkage || linkage.status === "matched_published_signal" || linkage.status === "matched_final_reply_claim" || linkage.status === "projection_pending") return null;
	if (isAuthoritativePublicationUnresolved(finalVisibleOutcome)) return null;
	if (linkage.status === "linkage_missing" || linkage.status === "storage_inconsistent") return {
		reasonCode: linkage.reasonCode ?? `final_visible_message.${linkage.status}`,
		source: "final_visible_message"
	};
	return null;
}
function isAuthoritativePublicationUnresolved(finalVisibleOutcome) {
	return finalVisibleOutcome.status === "publication_unresolved" && (finalVisibleOutcome.sourceFact === "runtime_coordinator_event" || finalVisibleOutcome.sourceFact === "runtime_coordinator_head_lifecycle" && finalVisibleOutcome.signalId === null);
}
function resolveLegacyResultInboxRootCause(trace) {
	if (hasRuntimeCoordinatorPreResultAuthority(trace)) return null;
	if (trace.resultInbox?.publishState === "late_result_available") return {
		reasonCode: "runtime_coordinator.late_result_available",
		source: "result_inbox"
	};
	if (trace.resultInbox?.publishState === "reply_publication_failed") return {
		reasonCode: "reply.publication_failed",
		source: "result_inbox"
	};
	if (trace.resultInbox?.publishState === "invalid_space_action") return {
		reasonCode: "reply.invalid_space_action",
		source: "result_inbox"
	};
	return null;
}
function resolveRuntimeProgressStaleRootCause(trace) {
	if (hasRuntimeCoordinatorPreResultAuthority(trace) && hasRuntimeCoordinatorProgressStalePolicyEvidence(trace)) return {
		reasonCode: "runtime.progress_stale",
		source: "runtime"
	};
	if (!hasRuntimeCoordinatorPreResultAuthority(trace) && trace.runtimeCoordinatorExecution) return null;
	if (trace.execution?.progressEvidenceState === "stalled") return {
		reasonCode: "runtime.progress_stale",
		source: "runtime"
	};
	if (trace.latestAttempt?.errorCode === "daemon.delivery.execution_stalled") return {
		reasonCode: "runtime.progress_stale",
		source: "runtime"
	};
	return null;
}
function hasRuntimeCoordinatorProgressStalePolicyEvidence(trace) {
	return trace.runtimeCoordinatorEvents.some((event) => event.eventType === "runtime_coordinator_decision_recorded" && event.reasonCode === "runtime_coordinator.progress_stale_policy_pending");
}
function resolveRuntimeCoordinatorHeadLifecycleRootCause(trace) {
	if (trace.runtimeCoordinatorExecution) return null;
	const headLifecycle = trace.runtimeCoordinatorHeadLifecycle;
	if (!headLifecycle) return null;
	if (headLifecycle.readStatus === "authority_unavailable") return {
		reasonCode: headLifecycle.reasonCode,
		source: "runtime_coordinator"
	};
	if (headLifecycle.readStatus === "missing") return {
		reasonCode: headLifecycle.reasonCode,
		source: "runtime_coordinator"
	};
	return {
		reasonCode: headLifecycle.noExecutionDecision?.reasonCode ?? headLifecycle.publicationIntent?.reasonCode ?? headLifecycle.waitingBoundary?.reasonCode ?? resolvePreExecutionNoExecutionEvidenceRootCause(trace) ?? resolveRuntimeCoordinatorHeadStatusRootCause(headLifecycle.status),
		source: "runtime_coordinator"
	};
}
function resolveRuntimeCoordinatorHeadStatusRootCause(status) {
	if (status === "queued") return "runtime_coordinator.head_queued";
	if (status === "accepted") return "runtime_coordinator.head_accepted";
	if (status === "execution_linked") return "runtime_coordinator.head_execution_linked";
	if (status === "waiting") return "runtime_coordinator.head_waiting";
	return "runtime_coordinator.head_publication_intent_ready";
}
function resolveRuntimeCoordinatorCreationRootCause(trace) {
	if (trace.runtimeCoordinatorExecution || trace.runtimeCoordinatorHeadLifecycle?.readStatus === "found" || trace.runtimeCoordinatorHeadLifecycle?.readStatus === "authority_unavailable" || trace.runtimeCoordinatorCreationDiagnostics.length === 0) return null;
	return {
		reasonCode: trace.runtimeCoordinatorCreationDiagnostics.at(-1)?.reasonCode ?? "runtime_coordinator.execution_not_created",
		source: "runtime_coordinator"
	};
}
const PRE_EXECUTION_NO_EXECUTION_EVIDENCE_REASON_CODES = /* @__PURE__ */ new Set(["local_service.identity_mismatch"]);
function resolvePreExecutionNoExecutionEvidenceRootCause(trace) {
	const headLifecycle = trace.runtimeCoordinatorHeadLifecycle;
	if (trace.runtimeCoordinatorExecution || headLifecycle?.readStatus !== "found" || headLifecycle.runtimeCoordinatorExecutionId || headLifecycle.waitingBoundary) return null;
	const authoritativeHeadReason = headLifecycle.noExecutionDecision?.reasonCode ?? headLifecycle.publicationIntent?.reasonCode ?? null;
	return authoritativeHeadReason && PRE_EXECUTION_NO_EXECUTION_EVIDENCE_REASON_CODES.has(authoritativeHeadReason) ? authoritativeHeadReason : null;
}
function resolveRuntimeCoordinatorExecutionRootCause(trace) {
	const execution = trace.runtimeCoordinatorExecution;
	if (!execution) return null;
	switch (execution.status) {
		case "failed_retryable":
		case "failed_terminal": return {
			reasonCode: execution.terminalOutcome?.reasonCode ?? `runtime_coordinator.${execution.status}`,
			source: "runtime_coordinator"
		};
		case "reply_published":
		case "cancelled": return null;
		case "state_unknown": return {
			reasonCode: "runtime_coordinator.state_unknown",
			source: "runtime_coordinator"
		};
		default: return {
			reasonCode: `runtime_coordinator.${execution.status}`,
			source: "runtime_coordinator"
		};
	}
}
function resolveLatestAttemptErrorRootCause(trace) {
	if (!trace.latestAttempt?.errorCode) return null;
	return {
		reasonCode: resolveAttemptErrorRootCause(trace.latestAttempt.errorCode) ?? trace.latestAttempt.errorCode,
		source: "latest_attempt"
	};
}
function resolveAttemptErrorRootCause(errorCode) {
	switch (errorCode) {
		case "daemon.delivery.execution_stalled": return "runtime.progress_stale";
		case "timeout":
		case "timed_out_no_output":
		case "timed_out_after_output": return "local_adapter.timeout";
		case "daemon.delivery.timeout":
		case "daemon.delivery.finalization_timeout": return "delivery.result_timeout";
		case "daemon.delivery.start_timeout": return "delivery.start_timeout";
		case "core.transport_restarting": return "runtime_transport.restarting";
		default: return null;
	}
}
function hasRuntimeCoordinatorPreResultAuthority(trace) {
	switch (trace.runtimeCoordinatorExecution?.status) {
		case "created":
		case "delivery_pending":
		case "delivery_accepted":
		case "cancellation_requested":
		case "accepted_local":
		case "running":
		case "completed_local": return true;
		default: return false;
	}
}
function resolveRuntimeCoordinatorPublicationUnresolvedBlocker(trace) {
	const event = trace.runtimeCoordinatorEvents.filter((candidate) => candidate.eventType === "runtime_coordinator_decision_recorded" && candidate.reasonCode === RUNTIME_COORDINATOR_PUBLICATION_UNRESOLVED_REASON_CODE).at(-1);
	if (!event) return null;
	const payload = parseRuntimeCoordinatorEventPayload(event);
	if (readPayloadString(payload, "status") !== "unresolved_publication_failure") return null;
	const publicationKind = readPayloadString(payload, "publicationKind");
	let parsedPublicationKind = "success_reply";
	if (publicationKind === "failure_notice") parsedPublicationKind = "failure_notice";
	else if (publicationKind === "stopped_notice") parsedPublicationKind = "stopped_notice";
	return {
		publicationKind: parsedPublicationKind,
		reasonCode: readPayloadString(payload, "reasonCode"),
		status: "unresolved_publication_failure"
	};
}
function parseRuntimeCoordinatorEventPayload(event) {
	try {
		const parsed = JSON.parse(event.payloadJson);
		return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
	} catch {
		return null;
	}
}
function readPayloadString(payload, fieldName) {
	const value = payload?.[fieldName];
	return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}
function readPayloadObject(payload, fieldName) {
	const value = payload?.[fieldName];
	return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}
function assertNever(value) {
	throw new Error(`Unexpected space dispatch trace value: ${String(value)}`);
}
function expectRuntimeCoordinatorHeadLifecycleTraceEvidence(context, value) {
	const headLifecycle = value.trace.runtimeCoordinatorHeadLifecycle;
	if (headLifecycle?.readStatus !== "found") return;
	expectTraceEvidence(context, headLifecycle.targetWakeLifecycleIdentity.spaceId, value.query.spaceId, ["trace", "runtimeCoordinatorHeadLifecycle"], "trace.runtimeCoordinatorHeadLifecycle.spaceId must match query.spaceId");
	expectTraceEvidence(context, headLifecycle.targetWakeLifecycleIdentity.sourceSignalId, value.trace.dispatch.sourceEventId, ["trace", "runtimeCoordinatorHeadLifecycle"], "trace.runtimeCoordinatorHeadLifecycle.sourceSignalId must match trace.dispatch.sourceEventId");
	expectTraceEvidence(context, headLifecycle.targetWakeLifecycleIdentity.targetProfileId, value.trace.dispatch.targetProfileId, ["trace", "runtimeCoordinatorHeadLifecycle"], "trace.runtimeCoordinatorHeadLifecycle.targetProfileId must match trace.dispatch.targetProfileId");
	if (headLifecycle.runtimeCoordinatorExecutionId === null) return;
	expectTraceEvidence(context, headLifecycle.runtimeCoordinatorExecutionId, value.trace.runtimeCoordinatorExecution?.runtimeCoordinatorExecutionId ?? null, ["trace", "runtimeCoordinatorHeadLifecycle"], "trace.runtimeCoordinatorHeadLifecycle.runtimeCoordinatorExecutionId must match trace.runtimeCoordinatorExecution");
}
function expectNativeDeliveryTraceEvidence(context, value) {
	const nativeDelivery = value.trace.nativeDelivery;
	if (!nativeDelivery) return;
	expectTraceEvidence(context, nativeDelivery.dispatchId, value.trace.dispatch.dispatchId, [
		"trace",
		"nativeDelivery",
		"dispatchId"
	], "trace.nativeDelivery.dispatchId must match trace.dispatch.dispatchId");
	expectTraceEvidence(context, nativeDelivery.destination.spaceId, value.query.spaceId, [
		"trace",
		"nativeDelivery",
		"destination",
		"spaceId"
	], "trace.nativeDelivery.destination.spaceId must match query.spaceId");
	expectTraceEvidence(context, nativeDelivery.destination.targetProfileId, value.trace.dispatch.targetProfileId, [
		"trace",
		"nativeDelivery",
		"destination",
		"targetProfileId"
	], "trace.nativeDelivery.destination.targetProfileId must match trace.dispatch.targetProfileId");
}
function expectFinalVisibleMessageTraceEvidence(context, trace) {
	if (!trace.finalVisibleMessage) return;
	expectTraceEvidence(context, trace.finalVisibleMessage.dispatchId, trace.dispatch.dispatchId, [
		"trace",
		"finalVisibleMessage",
		"dispatchId"
	], "trace.finalVisibleMessage.dispatchId must match trace.dispatch.dispatchId");
	expectTraceEvidence(context, trace.finalVisibleMessage.targetProfileId, trace.dispatch.targetProfileId, [
		"trace",
		"finalVisibleMessage",
		"targetProfileId"
	], "trace.finalVisibleMessage.targetProfileId must match trace.dispatch.targetProfileId");
	const finalVisibleSignalCandidates = listSpaceDispatchTraceFinalVisibleSignalCandidates(trace);
	if (trace.finalVisibleMessage.factSource !== "space_signal_by_published_signal_id" || finalVisibleSignalCandidates.length === 0) return;
	if (!finalVisibleSignalCandidates.includes(trace.finalVisibleMessage.signalId)) context.addIssue({
		code: "custom",
		message: "trace.finalVisibleMessage.signalId must match a canonical final visible publication signal",
		path: [
			"trace",
			"finalVisibleMessage",
			"signalId"
		]
	});
}
function expectTraceEvidence(context, actual, expected, path, message) {
	if (actual !== expected) context.addIssue({
		code: "custom",
		message,
		path
	});
}
function expectNullTraceEvidence(context, actual, path) {
	expectTraceEvidence(context, actual, null, path, "not-found trace correlation must not include resolved trace evidence");
}
//#endregion
export { SPACE_DISPATCH_TRACE_PROMPT_PREVIEW_MAX_CHARS, SPACE_DISPATCH_TRACE_RESPONSE_CONTRACT_VERSION, SPACE_DISPATCH_TRACE_RESPONSE_CONTRACT_VERSION_HEADER, SPACE_DISPATCH_TRACE_RESPONSE_CONTRACT_VERSION_PREVIOUS, listSpaceDispatchTraceFinalVisibleSignalCandidates, projectSpaceDispatchTraceFinalVisibleOutcome, projectSpaceDispatchTraceRootCause, resolveRuntimeCoordinatorHeadStatusRootCause, resolveSpaceDispatchTraceRootCause, spaceDispatchTraceAttemptStatusSchema, spaceDispatchTraceClaimLeaseSchema, spaceDispatchTraceDispatchExecutionSchema, spaceDispatchTraceDispatchSchema, spaceDispatchTraceDispatchStatusSchema, spaceDispatchTraceExecutionIdentitySchema, spaceDispatchTraceExecutionSchema, spaceDispatchTraceFinalVisibleMessageFactSourceSchema, spaceDispatchTraceFinalVisibleMessageLinkageSchema, spaceDispatchTraceFinalVisibleMessageLinkageSourceFactSchema, spaceDispatchTraceFinalVisibleMessageLinkageStatusSchema, spaceDispatchTraceFinalVisibleMessageSchema, spaceDispatchTraceFinalVisibleOutcomePublicationKindSchema, spaceDispatchTraceFinalVisibleOutcomeSchema, spaceDispatchTraceFinalVisibleOutcomeSourceFactSchema, spaceDispatchTraceFinalVisibleOutcomeStatusSchema, spaceDispatchTraceLatestAttemptSchema, spaceDispatchTraceNativeDeliverySchema, spaceDispatchTraceOpenClawBindingSchema, spaceDispatchTraceOpenClawGatewayVisibilitySchema, spaceDispatchTraceOpsDecisionSchema, spaceDispatchTraceOriginSchema, spaceDispatchTracePromptSchema, spaceDispatchTraceQuerySchema, spaceDispatchTraceReplySignalSchema, spaceDispatchTraceResponseContractVersionHeaderSchema, spaceDispatchTraceResponseContractVersionSchema, spaceDispatchTraceResponseSchema, spaceDispatchTraceResponseV1Schema, spaceDispatchTraceResponseV2Schema, spaceDispatchTraceResultInboxPublishStateSchema, spaceDispatchTraceResultInboxSchema, spaceDispatchTraceResultSchema, spaceDispatchTraceRuntimeCoordinatorCreationDiagnosticSchema, spaceDispatchTraceRuntimeCoordinatorEventSchema, spaceDispatchTraceRuntimeCoordinatorExecutionSchema, spaceDispatchTraceRuntimeCoordinatorHeadLifecycleSchema, spaceDispatchTraceRuntimeCoordinatorHeadLifecycleV1Schema, spaceDispatchTraceRuntimeCoordinatorHeadLifecycleV2Schema, spaceDispatchTraceRuntimeCoordinatorHeadNoExecutionDecisionV2Schema, spaceDispatchTraceRuntimeCoordinatorHeadPublicationIntentV2Schema, spaceDispatchTraceRuntimeSchema, spaceDispatchTraceSnapshotSchema, spaceDispatchTraceSnapshotV1Schema, spaceDispatchTraceSnapshotV2Schema, spaceDispatchTraceStreamingSchema, spaceDispatchTraceStructuredActionFailureSchema };

//# sourceMappingURL=space-dispatch-trace.js.map